package com.bikompanion.data

import com.bikompanion.data.cache.BikeDataCache
import com.bikompanion.domain.BikeNetwork
import com.bikompanion.domain.BikeNetworkRepository
import com.bikompanion.domain.BikeStation
import com.bikompanion.domain.RateLimitedException
import com.bikompanion.domain.Snapshot
import kotlin.coroutines.cancellation.CancellationException
import kotlin.time.Duration.Companion.hours

/**
 * How long a cached network list is trusted before a live [BikeNetworkRepository.fetchNetworks]
 * is worth making again.
 *
 * Network metadata (name, location, company, licence) barely changes, so re-downloading the
 * entire worldwide list on every app launch — as opposed to only when it's actually gone stale —
 * was pure waste. A day is generous against how often this data actually moves.
 */
private val NetworksCacheTtl = 24.hours

/**
 * Puts the on-disk cache in front of whichever source [BikeDataSource.Active] selects.
 *
 * A decorator rather than something either repository does for itself: caching is the same
 * problem for both, and keeping it out here is what lets the two sources stay swappable.
 *
 * The rule is write-through on success, fall back on failure. A refresh that fails while the
 * app is already running changes nothing on screen — the caller keeps the numbers it has —
 * but it is reported as stale so the UI can stop implying they are current. The cache only
 * really earns its place on a cold start with no connection, where without it the map would
 * open empty.
 */
internal class BikeNetworkRepositoryImpl(
    private val delegate: BikeNetworkRepository,
    private val cache: BikeDataCache,
) : BikeNetworkRepository {
    override suspend fun fetchNetworks(): List<BikeNetwork> {
        val cacheAgeMillis = cache.readNetworksAge()
        if (cacheAgeMillis != null && cacheAgeMillis < NetworksCacheTtl.inWholeMilliseconds) {
            // An empty list is never a usable cache hit: one written by an older build would
            // otherwise blank the map for the rest of its TTL, with no failure to retry.
            cache.readNetworks()?.takeIf { it.isNotEmpty() }?.let { return it }
        }
        return try {
            // An empty list from a 200 is a bad response, not news. Caching it would blank the
            // map for a day and survive restarts, so it is handled like any other failure.
            delegate
                .fetchNetworks()
                .also { check(it.isNotEmpty()) { "Network list came back empty" } }
                .also { cache.writeNetworks(it) }
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (error: Throwable) {
            // Network metadata is effectively static, so last week's list is as good as today's
            // and there is nothing for the user to retry. Only a first run with nothing stored
            // is a real failure.
            cache.readNetworks()?.takeIf { it.isNotEmpty() } ?: throw error
        }
    }

    override suspend fun fetchStations(networkId: String): Snapshot<List<BikeStation>> {
        val cached = cache.readStations(networkId)
        return try {
            val fresh = delegate.fetchStations(networkId)
            // A feed that suddenly reports no stations while we hold some is a failed scrape,
            // not a network that closed. Taking it would wipe the markers and the good copy.
            if (fresh.value.isEmpty() && cached?.stations?.isNotEmpty() == true) {
                return Snapshot(
                    value = cached.stations,
                    updatedAtEpochMillis = cached.updatedAtEpochMillis,
                    isStale = true,
                )
            }
            fresh.also { cache.writeStations(networkId, it.value, it.updatedAtEpochMillis) }
        } catch (cancellation: CancellationException) {
            throw cancellation
        } catch (error: Throwable) {
            cached ?: throw error
            Snapshot(
                value = cached.stations,
                updatedAtEpochMillis = cached.updatedAtEpochMillis,
                isStale = true,
                isRateLimited = error is RateLimitedException,
            )
        }
    }
}
